import React, { useState, useEffect, memo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, CheckCircle, History } from 'lucide-react';
import { DebouncedInput } from '../shared/DebouncedInputs';
import api from '../../../../utils/api';

// Input con estado local - solo actualiza padre en onBlur
const LocalInput = memo(({ initialValue = '', onBlur, type = 'text', ...props }) => {
  const [value, setValue] = useState(initialValue);
  
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);
  
  return (
    <Input
      {...props}
      type={type}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onBlur && onBlur(value)}
    />
  );
});

LocalInput.displayName = 'LocalInput';

const RegistrarPagoModal = memo(({ 
  open, 
  onOpenChange, 
  factura, 
  metodosPago,
  onSuccess 
}) => {
  const [nuevoPago, setNuevoPago] = useState({
    amount_clp: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'transferencia',
    reference: '',
    notes: ''
  });
  const [historialPagos, setHistorialPagos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // Cargar historial de pagos cuando se abre el modal
  useEffect(() => {
    if (open && factura?.id) {
      fetchHistorialPagos();
    }
  }, [open, factura?.id]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setNuevoPago({
        amount_clp: '',
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: 'transferencia',
        reference: '',
        notes: ''
      });
    }
  }, [open]);

  const fetchHistorialPagos = async () => {
    if (!factura?.id) return;
    setLoadingHistorial(true);
    try {
      const response = await api.get(`/finanzas/cobranzas-facturas/${factura.id}/pagos`);
      setHistorialPagos(response.data || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setHistorialPagos([]);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const handleRegistrarPago = async () => {
    if (!factura?.id || !nuevoPago.amount_clp) return;
    
    setLoading(true);
    try {
      await api.post(`/finanzas/cobranzas-facturas/${factura.id}/pagos`, {
        cobranza_id: factura.id,
        amount_clp: parseFloat(nuevoPago.amount_clp),
        payment_date: nuevoPago.payment_date,
        payment_method: nuevoPago.payment_method,
        reference: nuevoPago.reference,
        notes: nuevoPago.notes
      });
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error registering payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPagoTotal = useCallback(() => {
    const pendiente = factura?.pending_amount_clp ?? factura?.total_amount_clp ?? 0;
    setNuevoPago(prev => ({ ...prev, amount_clp: pendiente.toString() }));
  }, [factura]);

  const formatCLP = (amount) => new Intl.NumberFormat('es-CL').format(amount || 0);

  if (!factura) return null;

  const totalFactura = factura.total_amount_clp || factura.monto_clp || 0;
  const pendiente = factura.pending_amount_clp ?? totalFactura;
  const pagado = factura.paid_amount_clp || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <DollarSign className='w-5 h-5 text-green-600' />
            Registrar Pago de Factura
          </DialogTitle>
          <DialogDescription>
            Factura #{factura.numero_factura} - {factura.cliente?.company || factura.cliente?.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className='space-y-6'>
          {/* Info de la factura */}
          <Card className='p-4 bg-slate-50 border-slate-200'>
            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <p className='text-slate-500'>Monto Total</p>
                <p className='font-bold text-lg text-slate-900'>${formatCLP(totalFactura)}</p>
                <p className='text-xs text-slate-400'>
                  {(factura.original_amount_uf || factura.monto_uf)?.toFixed(2)} UF ref.
                </p>
              </div>
              <div>
                <p className='text-slate-500'>Monto Pendiente</p>
                <p className='font-bold text-lg text-amber-600'>${formatCLP(pendiente)}</p>
              </div>
              {pagado > 0 && (
                <>
                  <div>
                    <p className='text-slate-500'>Pagado</p>
                    <p className='font-bold text-green-600'>${formatCLP(pagado)}</p>
                  </div>
                  <div>
                    <p className='text-slate-500'>Estado</p>
                    <Badge 
                      variant='outline'
                      className={
                        factura.payment_status === 'paid'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : factura.payment_status === 'partially_paid'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                      }
                    >
                      {factura.payment_status === 'paid' 
                        ? 'Pagada' 
                        : factura.payment_status === 'partially_paid' 
                          ? 'Pago Parcial' 
                          : 'Pendiente'}
                    </Badge>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Historial de pagos */}
          {historialPagos.length > 0 && (
            <div>
              <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                <History className='w-4 h-4' />
                Historial de Pagos ({historialPagos.length})
              </h4>
              <div className='border rounded-lg overflow-hidden max-h-32 overflow-y-auto'>
                <table className='w-full text-sm'>
                  <thead className='bg-slate-50 sticky top-0'>
                    <tr>
                      <th className='text-left p-2 text-slate-600'>Fecha</th>
                      <th className='text-left p-2 text-slate-600'>Método</th>
                      <th className='text-right p-2 text-slate-600'>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialPagos.map((pago, idx) => (
                      <tr key={pago.id || idx} className='border-t'>
                        <td className='p-2'>{new Date(pago.payment_date).toLocaleDateString('es-CL')}</td>
                        <td className='p-2 capitalize'>{pago.payment_method || '-'}</td>
                        <td className='p-2 text-right font-medium text-green-600'>
                          ${formatCLP(pago.amount_clp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Formulario de nuevo pago */}
          {pendiente > 100 && (
            <div>
              <h4 className='text-sm font-semibold text-slate-700 mb-3'>Registrar Nuevo Pago</h4>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='amount_clp'>Monto a Pagar (CLP) *</Label>
                  <Input
                    id='amount_clp'
                    type='number'
                    placeholder='0'
                    value={nuevoPago.amount_clp}
                    onChange={(e) => setNuevoPago(prev => ({ ...prev, amount_clp: e.target.value }))}
                    data-testid='payment-amount-input'
                  />
                  <Button 
                    type='button' 
                    variant='outline' 
                    size='sm' 
                    className='w-full text-xs'
                    onClick={handleSetPagoTotal}
                  >
                    Pagar total pendiente
                  </Button>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='payment_date'>Fecha de Pago *</Label>
                  <Input
                    id='payment_date'
                    type='date'
                    value={nuevoPago.payment_date}
                    onChange={(e) => setNuevoPago(prev => ({ ...prev, payment_date: e.target.value }))}
                    data-testid='payment-date-input'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='payment_method'>Método de Pago</Label>
                  <Select
                    value={nuevoPago.payment_method}
                    onValueChange={(value) => setNuevoPago(prev => ({ ...prev, payment_method: value }))}
                  >
                    <SelectTrigger data-testid='payment-method-select'>
                      <SelectValue placeholder='Seleccionar método' />
                    </SelectTrigger>
                    <SelectContent>
                      {metodosPago.map((metodo) => (
                        <SelectItem key={metodo.value} value={metodo.value}>
                          {metodo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='payment_reference'>Referencia / N° Operación</Label>
                  <DebouncedInput
                    id='payment_reference'
                    placeholder='Ej: TRF-123456'
                    value={nuevoPago.reference}
                    onChange={(e) => setNuevoPago(prev => ({ ...prev, reference: e.target.value }))}
                    data-testid='payment-reference-input'
                  />
                </div>
                <div className='col-span-2 space-y-2'>
                  <Label htmlFor='payment_notes'>Notas (opcional)</Label>
                  <DebouncedInput
                    id='payment_notes'
                    placeholder='Observaciones adicionales...'
                    value={nuevoPago.notes}
                    onChange={(e) => setNuevoPago(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
          {pendiente > 100 && (
            <Button 
              onClick={handleRegistrarPago} 
              className='bg-green-600 hover:bg-green-700'
              disabled={loading || !nuevoPago.amount_clp}
              data-testid='confirmar-pago-btn'
            >
              <CheckCircle className='w-4 h-4 mr-2' />
              {loading ? 'Registrando...' : 'Registrar Pago'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

RegistrarPagoModal.displayName = 'RegistrarPagoModal';

export default RegistrarPagoModal;
